import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ArrowLeft } from 'lucide-react';

const NewKeyResultPage = () => {
  const { objectiveId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    description: '',
    owner_id: '',
    metric_name: '',
    initial_value: 0,
    target_value: 100,
    progress_type: 'cumulative'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const workspaceId = user?.workspaceId || user?.workspace?.id;
      if (!workspaceId) return;
      
      const response = await api.get(`/workspaces/${workspaceId}/users`);
      setUsers(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!formData.description || !formData.metric_name) {
      setError('Descripción y Métrica son requeridos');
      return;
    }
    
    // Ensure numeric values are numbers
    const initialValue = parseFloat(formData.initial_value);
    const targetValue = parseFloat(formData.target_value);
    
    if (isNaN(initialValue) || isNaN(targetValue)) {
      setError('Valor Inicial y Target deben ser números válidos');
      return;
    }
    
    if (initialValue === targetValue) {
      setError('El valor objetivo debe ser diferente al valor inicial');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Construct payload with proper field names
      const payload = {
        description: formData.description.trim(),
        metric_name: formData.metric_name.trim(),
        initial_value: initialValue,
        target_value: targetValue,
        current_value: initialValue, // Start at initial value
        owner_id: formData.owner_id || null, // Can be null if no owner selected
        progress_type: formData.progress_type
      };
      
      console.log('Creating KR with payload:', payload);
      
      await api.post(`/objectives/${objectiveId}/key-results`, payload);
      
      navigate(`/objetivos/${objectiveId}`);
    } catch (err) {
      console.error('Error creating KR:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Error al crear Key Result';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(`/objetivos/${objectiveId}`)}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Volver</span>
      </button>

      <h1 className="text-2xl font-bold">Nuevo Key Result</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descripción <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Ejemplo: Aumentar ventas mensuales"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
            rows={3}
            required
          />
        </div>

        {/* Owner */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Responsable
          </label>
          <select
            value={formData.owner_id}
            onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Sin asignar</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
        </div>

        {/* Metric */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Métrica <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.metric_name}
            onChange={(e) => setFormData({ ...formData, metric_name: e.target.value })}
            placeholder="Ejemplos: %, Horas, Unidades, Reuniones, etc."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            La unidad de medida (ej: porcentaje, horas, unidades)
          </p>
        </div>

        {/* Initial and Target Values */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Valor Inicial <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.initial_value}
              onChange={(e) => setFormData({ ...formData, initial_value: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.target_value}
              onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
        </div>

        {/* Progress Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Progreso
          </label>
          <select
            value={formData.progress_type}
            onChange={(e) => setFormData({ ...formData, progress_type: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          >
            <option value="cumulative">Acumulativo</option>
            <option value="snapshot">Instantáneo</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Acumulativo: el progreso se suma. Instantáneo: cada medición reemplaza la anterior.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/objetivos/${objectiveId}`)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
          >
            {loading ? 'Creando...' : 'Crear Key Result'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewKeyResultPage;
